import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import LoadingDots from '../../LoadingDots/LoadingDots';
import { collectImageSources } from '../../../utils/imagePreload';
import { useImagePreload } from '../../../hooks/useImagePreload';
import ScrollFadeWrapper from '../../ScrollFadeWrapper/ScrollFadeWrapper';
import Image from '../../Image/Image';

const SeoSnakeComponent = ({ data }) => {
  const cards = Array.isArray(data)
    ? data
    : (data?.cards ?? data?.guides ?? data?.items ?? []);
  const imageSources = useMemo(() => collectImageSources(cards), [cards]);
  const areImagesReady = useImagePreload(imageSources);

  const getJustifyContent = (align) => {
    switch ((align || '').toLowerCase()) {
      case 'left':
        return 'flex-start';
      case 'center':
        return 'center';
      case 'right':
      default:
        return 'flex-end';
    }
  };

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      {!areImagesReady ? (
        <Box
          sx={{
            width: '100%',
            minHeight: '320px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <LoadingDots variant="otp" />
        </Box>
      ) : (
        <Box sx={{ width: '1100px', boxSizing: 'border-box' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
            {cards.map((card, index) => (
              <ScrollFadeWrapper key={index}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: getJustifyContent(card.align),
                    position: 'relative',
                  }}
                >
                  {/* Image with overlap */}
                  <Box
                    sx={{
                      width: '286px',
                      height: '224px',
                      borderRadius: '34px',
                      backgroundColor: '#A2ACB1',
                      overflow: 'hidden',
                      flexShrink: 0,
                      zIndex: 2,
                      position: 'relative',
                      marginRight: '-80px',
                    }}
                  >
                    <Image
                      width={286}
                      height={224}
                      src={card.image}
                      alt={card.imgAltTag || `Card ${index + 1}`}
                      title={card.imageAltTitle}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  </Box>

                  {/* White card */}
                  <Box
                    sx={{
                      width: '732px',
                      minHeight: '260px',
                      backgroundColor: '#FFFFFF',
                      borderRadius: '34px',
                      boxShadow:
                        '0px 1px 2px 0px #A2ACB133, 0px 12px 24px -4px #162F3B1F',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      paddingTop: '40px',
                      paddingBottom: '40px',
                      paddingLeft: '104px',
                      paddingRight: '40px',
                      boxSizing: 'border-box',
                      zIndex: 1,
                    }}
                  >
                    <Box>
                      <Typography
                        component="div"
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 700,
                          fontSize: '1.5rem',
                          lineHeight: '1.75rem',
                          letterSpacing: '-0.003em',
                          color: '#162F3B',
                          mb: '16px',
                          textAlign: 'left',
                          '& p': { margin: 0 },
                        }}
                        dangerouslySetInnerHTML={{ __html: card.title || '' }}
                      ></Typography>

                      <Typography
                        component="div"
                        sx={{
                          fontFamily: 'Inter, sans-serif',
                          fontWeight: 400,
                          fontSize: '1rem',
                          lineHeight: '1.5rem',
                          letterSpacing: '-0.002em',
                          color: '#162F3B',
                          textAlign: 'left',
                          whiteSpace: 'pre-line',
                          '& p': { margin: 0, mb: '1em' },
                          '& p:last-of-type': { mb: 0 },
                          '& strong': { fontWeight: 700 },
                          '& ul': {
                            m: 0,
                            mt: '1em',
                            pl: 0,
                            listStylePosition: 'inside',
                          },
                          '& a': {
                            color: '#C7CB00',
                            textDecoration: 'underline',
                            fontWeight: 700,
                          },
                          '& a:hover': {
                            opacity: 0.85,
                          },
                        }}
                        dangerouslySetInnerHTML={{ __html: card.body || '' }}
                      ></Typography>
                    </Box>

                    {/* Optional link */}
                    {card.redirect ? (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Typography
                          component="a"
                          href={card.redirect}
                          sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                            fontSize: '1rem',
                            lineHeight: '1.5rem',
                            letterSpacing: '-0.002em',
                            color: '#162F3B',
                            textDecoration: 'none',
                            mt: '16px',
                            '&:hover': {
                              color: '#9EA900',
                              textDecoration: 'underline',
                            },
                          }}
                        >
                          Learn more about {card.title}
                        </Typography>
                      </Box>
                    ) : null}
                  </Box>
                </Box>
              </ScrollFadeWrapper>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SeoSnakeComponent;
